import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Eye, Factory, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input, Select } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import { productionService } from '@services/productionService';
import {
  ProductionOrder,
  PRODUCTION_ORDER_STATUS_OPTIONS,
  getProductionOrderStatusLabel,
  getProductionOrderStatusBadgeClass,
} from '@/types/production';

/** Formata data ISO para dd/mm/aaaa sem deslocamento de fuso (parse manual) */
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return '-';
};

const formatQty = (value?: number): string =>
  value !== undefined && value !== null ? value.toLocaleString('pt-BR') : '0';

/**
 * Listagem de Ordens de Produção
 */
export const ProductionOrderListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<ProductionOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await productionService.listProductionOrders({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
        status: statusFilter !== '' ? parseInt(statusFilter) : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ordens de produção');
    } finally {
      setLoading(false);
    }
  };

  // Carrega quando página ou filtros mudam
  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, dateFrom, dateTo]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadItems();
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Ordens de Produção</h1>
          <p className="text-slate-600">
            Planeje e acompanhe a produção do atelier (cabides e semiacabados)
          </p>
        </div>
        <PermissionGuard permission="production.create">
          <Button onClick={() => navigate('/production/orders/new')} icon={<Plus size={20} />}>
            Nova OP
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros e busca */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              label="Buscar"
              placeholder="Código, descrição ou cor do floco..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startIcon={<Search size={20} />}
            />
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">Todos</option>
              {PRODUCTION_ORDER_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <div>
              <label className="text-label block mb-1">Data inicial</label>
              <DateInputBR
                className="input w-full"
                value={dateFrom}
                onChange={(v) => {
                  setCurrentPage(1);
                  setDateFrom(v);
                }}
              />
            </div>
            <div>
              <label className="text-label block mb-1">Data final</label>
              <DateInputBR
                className="input w-full"
                value={dateTo}
                onChange={(v) => {
                  setCurrentPage(1);
                  setDateTo(v);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando ordens de produção...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <Factory className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search ? 'Nenhuma ordem encontrada' : 'Nenhuma ordem de produção cadastrada'}
              </p>
              <PermissionGuard permission="production.create">
                <Button onClick={() => navigate('/production/orders/new')}>
                  Criar primeira OP
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Código</th>
                  <th className="text-left">Descrição</th>
                  <th className="text-left">Cor do Floco</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Início Previsto</th>
                  <th className="text-center">Fim Previsto</th>
                  <th className="text-right">Planejado</th>
                  <th className="text-right">Produzido</th>
                  <th className="text-right">Perdido</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {items.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs">{order.code}</code>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{order.description || '-'}</p>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-700">{order.flock_color || '-'}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getProductionOrderStatusBadgeClass(
                          order.status
                        )}`}
                      >
                        {getProductionOrderStatusLabel(order.status)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-700">
                        {formatDate(order.planned_start_date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-700">
                        {formatDate(order.planned_end_date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-slate-900">
                        {formatQty(order.total_planned)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-emerald-700">
                        {formatQty(order.total_produced)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {order.total_lost ? (
                        <span className="font-medium text-red-600">
                          {formatQty(order.total_lost)}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/production/orders/${order.id}`)}
                          icon={<Eye size={14} />}
                        >
                          Ver
                        </Button>
                        {(order.status === 0 || order.status === 1) && (
                          <PermissionGuard permission="production.write">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => navigate(`/production/orders/${order.id}/edit`)}
                              icon={<Edit size={14} />}
                            >
                              Editar
                            </Button>
                          </PermissionGuard>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
